import { supabase } from "../supabase/supabaseClient";

const FOLLOWS_TABLE = "user_follows";
const FOLLOW_TYPE_USER = "user";
const FOLLOW_TYPE_ORG = "org";

const getCurrentUserId = async () => {
    const { data } = await supabase.auth.getSession();
    return data?.session?.user?.id || null;
};

export const isFollowingUser = async (otherUserId) => {
    const currentUserId = await getCurrentUserId();
    if (!currentUserId || !otherUserId) return false;

    const { data, error } = await supabase
        .from(FOLLOWS_TABLE)
        .select("follower_id")
        .eq("follower_id", currentUserId)
        .eq("followee_type", FOLLOW_TYPE_USER)
        .eq("followee_id", otherUserId)
        .limit(1)
        .maybeSingle();

    if (error) {
        console.error("Error checking user follow status:", error);
        return false;
    }

    return Boolean(data);
};

export const followUser = async (otherUserId) => {
    const currentUserId = await getCurrentUserId();
    if (!currentUserId || !otherUserId || currentUserId === otherUserId) {
        return { success: false, error: new Error("Invalid follow request.") };
    }

    const { data, error } = await supabase
        .from(FOLLOWS_TABLE)
        .insert([
            {
                follower_id: currentUserId,
                followee_type: FOLLOW_TYPE_USER,
                followee_id: otherUserId,
            }
        ])
        .select()
        .limit(1);

    if (error) {
        if (error.code === "23505") {
            return { success: true, data: data || null, error: null };
        }
        return { success: false, data: null, error };
    }

    return { success: true, data: data || null, error: null };
};

export const unfollowUser = async (otherUserId) => {
    const currentUserId = await getCurrentUserId();
    if (!currentUserId || !otherUserId) {
        return { success: false, error: new Error("Invalid unfollow request.") };
    }

    const { data, error } = await supabase
        .from(FOLLOWS_TABLE)
        .delete()
        .eq("follower_id", currentUserId)
        .eq("followee_type", FOLLOW_TYPE_USER)
        .eq("followee_id", otherUserId)
        .select();

    if (error) {
        return { success: false, data: null, error };
    }

    return { success: true, data: data || null, error: null };
};

export const getFollowingUserIds = async () => {
    const currentUserId = await getCurrentUserId();
    if (!currentUserId) return [];

    const { data, error } = await supabase
        .from(FOLLOWS_TABLE)
        .select("followee_id")
        .eq("follower_id", currentUserId)
        .eq("followee_type", FOLLOW_TYPE_USER);

    if (error) {
        console.error("Error fetching followed user ids:", error);
        return [];
    }

    return Array.from(new Set((data || []).map((row) => row.followee_id).filter(Boolean)));
};

export const isFollowingOrg = async (orgId) => {
    const currentUserId = await getCurrentUserId();
    if (!currentUserId || !orgId) return false;

    const { data, error } = await supabase
        .from(FOLLOWS_TABLE)
        .select("follower_id")
        .eq("follower_id", currentUserId)
        .eq("followee_type", FOLLOW_TYPE_ORG)
        .eq("followee_id", orgId)
        .limit(1)
        .maybeSingle();

    if (error) {
        console.error("Error checking org follow status:", error);
        return false;
    }

    return Boolean(data);
};

export const followOrg = async (orgId) => {
    const currentUserId = await getCurrentUserId();
    if (!currentUserId || !orgId) {
        return { success: false, error: new Error("Invalid follow request.") };
    }

    const { data, error } = await supabase
        .from(FOLLOWS_TABLE)
        .insert([
            {
                follower_id: currentUserId,
                followee_type: FOLLOW_TYPE_ORG,
                followee_id: orgId,
            }
        ])
        .select()
        .limit(1);

    if (error) {
        if (error.code === "23505") {
            return { success: true, data: data || null, error: null };
        }
        return { success: false, data: null, error };
    }

    return { success: true, data: data || null, error: null };
};

export const unfollowOrg = async (orgId) => {
    const currentUserId = await getCurrentUserId();
    if (!currentUserId || !orgId) {
        return { success: false, error: new Error("Invalid unfollow request.") };
    }

    const { data, error } = await supabase
        .from(FOLLOWS_TABLE)
        .delete()
        .eq("follower_id", currentUserId)
        .eq("followee_type", FOLLOW_TYPE_ORG)
        .eq("followee_id", orgId)
        .select();

    if (error) {
        return { success: false, data: null, error };
    }

    return { success: true, data: data || null, error: null };
};
